import React, { useState, useEffect, useRef } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useSpring, animated } from "react-spring";

import Home from "./Screens/Home";
import Certified from "./Screens/Certified";
import Standard from "./Screens/Standard";
import Interpretations from "./Screens/Interpretations";
import Languages from "./Screens/Languages";
import UseCasesPage from "./Screens/UseCases";
import FAQ from "./Screens/FAQ";
import About from "./Screens/About";
import Business from "./Screens/Business";
import OrderLookup from "./Screens/OrderLookup";
import Verify from "./Screens/Verify";
import Contact from "./Screens/Contact";
import Terms from "./Screens/Terms";
import Refunds from "./Screens/Refunds";
import Privacy from "./Screens/Privacy";
import Reviews from "./Screens/Reviews";
import UseCasesRouter from "./Routers/UseCasesRouter";

import Profile from "./Screens/Account/Profile";
import Quotes from "./Screens/Account/Quotes";
import SingleQuote from "./Screens/Account/SingleQuote";
import Orders from "./Screens/Account/Orders";
import SingleOrder from "./Screens/Account/SingleOrder";
import Shipping from "./Screens/Account/Shipping";

import QuoteContact from "./Screens/Quote/QuoteContact";
import QuoteDetails from "./Screens/Quote/QuoteDetails";

import OrderContact from "./Screens/Order/OrderContact";
import OrderDocuments from "./Screens/Order/OrderDocuments";
import OrderOptions from "./Screens/Order/OrderOptions";
import OrderPayment from "./Screens/Order/OrderPayment";

import { TransitionOverlay } from "./Components/TransitionOverlay";
import { NotificationBox } from "./Components/NotificationBox";

import { TransitionContext } from "./Context/TransitionContext";
import { NotificationContext } from "./Context/NotificationContext";
import { AuthContext } from "./Context/AuthContext";
import { DataContext } from "./Context/DataContext";

import { PrivateRoute } from "./Components/PrivateRoute";
import { HeadedRoute } from "./Components/HeadedRoute";

import "./App.css";
import JoinTeam from "./Screens/JoinTeam";
import Password from "./Screens/Account/Password";
import OrderType from "./Screens/Order/OrderType";
import QuoteOptions from "./Screens/Quote/QuoteOptions";
import Setting from "./Screens/Account/Settings";
import TeamMembers from "./Screens/Account/TeamMembers";
import MakePayment from "./Screens/Account/MakePayment";
import AccountInvoices from "./Screens/Account/Invoices";
import SingleInvoice from "./Screens/Account/SingleInvoice";

import { pdfjs } from "react-pdf";
import QuoteCreate from "./Screens/Quote/backend/QuoteCreate";
import QuoteCreateDetails from "./Screens/Quote/backend/QuoteCreateDetails";
import QuoteService from "./Screens/Quote/backend/QuoteService";
import { AccountType, EAST1, EAST4 } from "./utility/enums";
import NotFound from "./Screens/NotFound";
import Loader from "./NewComponents/Loader";
import { auth, firestore, getFullHttpUrl, PAYPAL_OPTIONS, STRIPE_ID } from "./firebase.config";
import FAQs from "./Screens/FAQs/Faq";
import WarningHeader from "./NewComponents/WarningHeader";
import Coupons from "./Screens/Backend/Coupons";
import SingleTeam from "./Screens/Backend/SingleTeam";
import Teams from "./Screens/Backend/Teams";
import Users from "./Screens/Backend/Users";
import SingleRevision from "./Screens/Backend/SingleRevision";
import Preferences from "./Screens/Backend/Preferences";
import SingleTranslation from "./Screens/Backend/SingleTranslation";
import SingleQuoteBackend from "./Screens/Backend/SingleQuote";
import SinglePayment from "./Screens/Backend/SinglePayment";
import SingleNotification from "./Screens/Backend/SingleNotification";
import Notifications from "./Screens/Backend/Notifications";
import AdminNotifications from "./NewComponents/AdminNotification";
import SingleUser from "./Screens/Backend/SingleUser";
import ManualOrder from "./Screens/Backend/ManualOrder";
import SingleClient from "./Screens/Backend/SingleClient";
import Guidelines from "./Screens/Backend/Guidelines";
import OwnRevisions from "./Screens/Backend/OwnRevisions";
import OwnTranslations from "./Screens/Backend/OwnTranslations";
import OwnInvoices from "./Screens/Backend/OwnInvoices";
import Employees from "./Screens/Backend/Employees";
import SingleInvoiceBackend from "./Screens/Backend/SingleInvoice";
import OfficeInvoices from "./Screens/Backend/Invoices";
import QuotesBackend from "./Screens/Backend/Quotes";
import OrdersBackend from "./Screens/Backend/Orders";
import SingleOrderBackend from "./Screens/Backend/SingleOrder";
import OwnPayments from "./Screens/Backend/OwnPayments";
import Clients from "./Screens/Backend/Clients";
import Payments from "./Screens/Backend/Payments";
import Dashboard from "./Screens/Backend/Dashboard";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import MaintenancePage from "./Screens/Order/MaintainacePage";
import LegalDocumentTranslationPage from "./Screens/LegalDocumentTranslation";
import MedicalDocumentTranslationPage from "./Screens/MedicalDocumentTranslationPage";
import BirthCertificateDocumentTranslationPage from "./Screens/BirthCertificateDocumentTranslation";
import ImmigrationDocumentTranslationPage from "./Screens/ImmigrationDocumentTranslation";
import FinancialDocumentTranslationPage from "./Screens/FinancialDocumentTranslation";
import CustomToast from "./Components/shared/customToast";
import { collectAllUserDetails } from "./utility/GETUSERDETAILS";
import BlockedUsers from "./Screens/Backend/BlockedUsers";
import OnBoardingScreen from "./Screens/CreateAccount/OnBoardingScreens";
import { useScreenTransition } from "./Hooks/useScreenTransition";
import MarketingLandingPage from "./Screens/MarketingLandingPage";
import ChangePasswordWarning from "./NewComponents/ChangePasswordWarning";


function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const App = () => {
  const [windowSize, setWindowSize] = useState({
    height: window.document.documentElement.clientHeight,
    width: window.document.documentElement.clientWidth,
    orientation:
      window.document.documentElement.clientWidth >
      window.document.documentElement.clientHeight
        ? "landscape"
        : "portrait",
  });

  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

  const [color, setColor] = useState(false);

  const [showPreloader, setShowPreloader] = useState(false);
  const preloaderProps = useSpring({
    opacity: showPreloader ? 1 : 0,
    pointerEvents: showPreloader ? "all" : "none",
  });

  const [webP, setWebP] = useState(null);

  useEffect(() => {
    const checkForWebP = async () => {
      if (!window.self.createImageBitmap) {
        setWebP("png");
        return false;
      }

      const webpData =
        "data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=";
      const blob = await fetch(webpData).then((r) => r.blob());
      const supportsWebP = await createImageBitmap(blob).then(
        () => true,
        () => false
      );

      setWebP(supportsWebP ? "webp" : "png");
    };

    checkForWebP();
  }, []);

  useEffect(() => {
    sleep(1000).then(() => {
      setShowPreloader(false);
    });
  }, []);

  useEffect(() => {
    setWindowSize({
      height: window.document.documentElement.clientHeight,
      width: window.document.documentElement.clientWidth,
      orientation:
        window.document.documentElement.clientWidth >
        window.document.documentElement.clientHeight
          ? "landscape"
          : "portrait",
    });
  }, []);

  const [manualQuote, setManualQuote] = useState({
    parsed: false,
  });

  const [quote, setQuote] = useState({
    name: "",
    email: "",
    parsed: false,
  });

  const [order, setOrder] = useState({
    orderID: "",
    name: "",
    email: "",
    parsed: false,
  });

  useEffect(() => {
    //localStorage.clear();
    if (localStorage.getItem("order")) {
      let mOrder = JSON.parse(localStorage.getItem("order"));

      if (mOrder.files && typeof mOrder.files === "string") {
        try {
          mOrder.files = JSON.parse(mOrder.files);
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }

      setOrder({ ...mOrder, parsed: true });
    } else {
      localStorage.setItem("order", JSON.stringify({ ...order, parsed: true }));
      setOrder({ ...order, parsed: true });
    }

    if (localStorage.getItem("quote")) {
      let mQuote = JSON.parse(localStorage.getItem("quote"));

      if (mQuote.files) {
        mQuote.files = JSON.parse(mQuote.files);
      }

      setQuote({ ...mQuote, parsed: true });
    } else {
      localStorage.setItem("quote", JSON.stringify({ ...quote, parsed: true }));
      setQuote({ ...quote, parsed: true });
    }

    if (localStorage.getItem("manualQuote")) {
      let mQuote = JSON.parse(localStorage.getItem("manualQuote"));

      if (mQuote.files) {
        mQuote.files = JSON.parse(mQuote.files);
      }

      setManualQuote({ ...mQuote, parsed: true });
    } else {
      localStorage.setItem("quote", JSON.stringify({ ...manualQuote, parsed: true }));
      setManualQuote({ ...manualQuote, parsed: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [signedIn, setSignedIn] = useState(true);

  const clearSignOut = () => {
    setOrder({ orderID: "", name: "", email: "", parsed: false });
    setQuote({ name: "", email: "", parsed: false });
    setUserData(null);
    localStorage.clear();
  };

  const [userData, setUserData] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(false);
  let unsubscribeUserData = useRef({});
  let unsubscribeTeamData = useRef({});

  useEffect(() => {
    let unSub;
    try{

      setLoadingAuth(true);
      unSub = auth.onAuthStateChanged(async(user) => {
      if (user && user.uid) {
        subscribeUserData(user.uid);
        setSignedIn(true);

        const docRef = firestore.collection("users").doc(user.uid);

        await docRef
          .get()
          .then(async (doc) => {
            if (!doc.exists) {
              await docRef.set({ email: user.email }, { merge: true });
              setLoadingAuth(false);
            }
          })
          .catch((error) => {
            setLoadingAuth(false);
            auth.signOut();
            clearSignOut();
          });
        } else {
          setSignedIn(false);
          setLoadingAuth(false);
        }

        // Set loading false once userData is set
        // setTimeout(() => {
        //   setLoadingUser(false);
        // }, 1000);
    })
    }catch(e){
      console.log(e);
    }finally{
      setLoadingAuth(false);
    }

    return () => {
      unSub && unSub();
      if (typeof unsubscribeTeamData.current === "function") {
        unsubscribeTeamData.current();
      }
      if (typeof unsubscribeTeamData.current === "function") {
        unsubscribeTeamData.current();
      }
    };
  }, []);

  const subscribeUserData = (userID) => {
    try{
      setLoadingUser(true);
      unsubscribeUserData.current = firestore
      .collection("users")
      .doc(userID)
      .onSnapshot(function (doc) {
        if (doc.exists) {
          setUserData({ ...doc.data(), id: userID });
          if(doc.data().teamId){
            subscribeTeamData(doc.data().teamId);
          }else{
            setTeamData(null);
            if (typeof unsubscribeTeamData.current === "function") {
              unsubscribeTeamData.current();
            }
            unsubscribeTeamData.current = {};
          }
        }
      });
    }catch(e){
      console.log(e)
    }finally{
      setUserLoaded(true);
      setLoadingUser(false);
    }
  };
  const subscribeTeamData = (teamId) => {
    try{
      setLoadingTeam(true);
      unsubscribeTeamData.current = firestore
        .collection("teams")
        .doc(teamId)
        .onSnapshot(function (doc) {
          if (doc.exists) {
            setTeamData({ ...doc.data(), id: teamId });
          }
        });
    }catch(e){
      console.log(e)
    }finally{
      setUserLoaded(true);
      setLoadingTeam(false);
    }
  };

  // useEffect(() => {
  //   if (signedIn && userData && userData.teamId) {
  //     setLoadingTeam(true);
  //     subscribeTeamData(userData.teamId);
  //   } else {
  //     if (typeof unsubscribeTeamData.current === "function") {
  //       unsubscribeTeamData.current();
  //     }
  //     unsubscribeTeamData.current = {};
  //   }

  //   return () => {
  //     if (typeof unsubscribeTeamData.current === "function") {
  //       unsubscribeTeamData.current();
  //     }
  //   };
  // }, [userData, signedIn]);


  const [showTransitionOverlay, setShowTransitionOverlay] = useState(false);

  useEffect(() => {
    const onResize = () => {
      setWindowSize({
        height: window.document.documentElement.clientHeight,
        width: window.document.documentElement.clientWidth,
        orientation:
          window.document.documentElement.clientWidth >
          window.document.documentElement.clientHeight
            ? "landscape"
            : "portrait",
      });
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const setBodyLock = (status) => {
    if (status) {
      document.getElementById("app").style.height = `${windowSize.height}px`;
      document.getElementById("app").style.overflow = `hidden`;
    } else {
      document.getElementById("app").style.height = `auto`;
      document.getElementById("app").style.overflow = `auto`;
    }
  };

  const [notification, setNotification] = useState({
    show: false,
    title: "Success Notification",
    text: "This is a test alert to see how it works.",
    type: "error",
  });


  const showNotification = (title, text, type, sendErrorEmail, error = "",) => {
    setNotification({ show: true, title: title, text: text, type: type });
    
    const pageUrl = window ? window.location.href : ""
    if(type === "error" && userData ){
      axios.post(getFullHttpUrl("sendErrorEmail", EAST4), 
      {userData: userData || {name: "guest", email: "guest"}, error, notificationText: text, 
      errorMessage: error ? error.message || "": title, pageUrl});
    }

    sleep(5000).then(() => {
      setNotification({ show: false, title: title, text: text, type: type });
    });
  };

  const [userBlocked, setUserBlocked] = useState(false);

  const checkUserBlocked = async () => {
    firestore.collection("blockedUsers").doc(userData.email).get().then((doc) => {
        if (doc.exists) {
            collectAllUserDetails(doc.data(), userData.email, userData.name, false);
            setUserBlocked(true);
        }
    });
}

const checkIpBlocked = async () => {
  const response = await axios.get('https://api.ipify.org/?format=json');
  firestore.collection("blockedIps").doc(response.data.ip).get().then((doc) => {
    if (doc.exists) {
      setUserBlocked(true);
    } else {
      if(userData && userData.email){
        checkUserBlocked();
      }
    }
  });
}

useEffect(() => {
  checkIpBlocked();
}, [userData]);

const checkUserName = (data) => {
  const name = data.name ? data.name.toLowerCase() : data.firstName ? (data.firstName + " " + data.lastName).toLowerCase() : "";
  if(name){
    firestore.collection("blockedNames").where("name", "==", data.name.toLowerCase()).get().then((qSnap) => {
      if(qSnap.empty){
        return;
      }
      if(qSnap.docs.length > 0){
        collectAllUserDetails("", data.email, name, true);
        // setUserBlocked(true);
      }
    })
  }
  return;
}

useEffect(() => {
    quote && quote.parsed && checkUserName(quote);
    order && order.parsed && checkUserName(order);
}, [quote, order]);


  // return ( <MaintenancePage/>);


  if (loadingUser || loadingTeam || loadingAuth || (!userData && signedIn) || userBlocked) {
    return (
      <div className="container mx-auto w-full h-dvh flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  

  return (
    <>

    {/*<WarningHeader/>*/}
    <TransitionContext.Provider
      value={{
        setShowTransitionOverlay: setShowTransitionOverlay,
        setBodyLock: setBodyLock,
        color: color,
        setColor: setColor,
        webP: webP,
      }}
    >
      <NotificationContext.Provider
        value={{ showNotification: showNotification }}
      >
        <AuthContext.Provider
          value={{
            signedIn: signedIn,
            setSignedIn: setSignedIn,
            clearSignOut: clearSignOut,
          }}
        >
          <DataContext.Provider
            value={{
              quote: quote,
              setQuote: setQuote,
              order: order,
              setOrder: setOrder,
              userData: userData,
              userLoaded: userLoaded,
              teamData: teamData,
              manualQuote: manualQuote,
              setManualQuote: setManualQuote,
            }}
          >
            <PayPalScriptProvider
              options={PAYPAL_OPTIONS}
            >
              <div id="app" className="appContainer">
                <animated.div
                  style={preloaderProps}
                  className="preloaderOverlay"
                />

                <ChangePasswordWarning/>
                <CustomToast />

                <TransitionOverlay show={showTransitionOverlay} />

                <NotificationBox
                  windowSize={windowSize}
                  show={notification.show}
                  title={notification.title}
                  text={notification.text}
                  type={notification.type}
                />


                {/* <Elements stripe={stripePromise}> */}
                  <HelmetProvider>
                    <Router>
                      <Switch>
                        {/* {userData &&
                          (userData.role === AccountType.OWNER ||
                            userData.role === AccountType.SUPPORT ||
                            userData.role === AccountType.TRANSLATER) && (
                            <PrivateRoute path="/office">
                              <Office windowSize={windowSize} />
                            </PrivateRoute>
                          )} */}

                          

                       {
                        userData && teamData && teamData.newTeam
                       && (userData.teamRole === AccountType.OWNER || userData.teamRole === AccountType.ADMIN) &&
                          <PrivateRoute path="/account/onboarding">
                            <OnBoardingScreen windowSize={windowSize}/>
                          </PrivateRoute>
                        }
                      

                        {/* Start of Admin Routes */}

                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/dashboard`}>
                            <Dashboard windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/payments`}>
                            <Payments windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/clients`}>
                            <Clients windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/payments`}>
                            <OwnPayments windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/translations/:orderID`}>
                            <SingleOrderBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/translations`}>
                            <OrdersBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/quotes`}>
                            <QuotesBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/invoices`}>
                            <OfficeInvoices windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/invoice/:itemID`}> 
                            <SingleInvoiceBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === 'SupportPlus') &&
                          <PrivateRoute path={`/office/employees`}>
                            <Employees windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === 'SupportPlus') &&
                          <PrivateRoute path={`/office/admin-notifications`}>
                            <AdminNotifications windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/invoices`}>
                            <OwnInvoices windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/translations`}>
                            <OwnTranslations windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/revisions`}>
                            <OwnRevisions windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/guidelines`}>
                            <Guidelines windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/client/:clientID`}>
                            <SingleClient windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/new-order`}>
                            <ManualOrder windowSize={windowSize} mode={"order"} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/new-quote`}>
                            <ManualOrder windowSize={windowSize} mode={"quote"} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.OWNER &&
                          <PrivateRoute path={`/office/user/:userID`}>
                            <SingleUser windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        <PrivateRoute path={`/office/notifications`}>
                          <Notifications windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/notification/:notificationID`}>
                          <SingleNotification windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/payment/:paymentID`}>
                          <SinglePayment windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/quote/:quoteID`}>
                          <SingleQuoteBackend windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/translation/:id/:tabParam`}>
                          <SingleTranslation windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/preferences`}>
                          <Preferences windowSize={windowSize} />
                        </PrivateRoute>
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/revision/:id`}>
                            <SingleRevision windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/teams`}>
                            <Teams windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER ) &&
                          <PrivateRoute path={`/office/team/:id`}>
                            <SingleTeam windowSize={windowSize} />
                          </PrivateRoute>
                        }
                          {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                            <PrivateRoute path={`/office/coupons`}>
                              <Coupons windowSize={windowSize} />
                            </PrivateRoute>
                          }
                          {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                            <PrivateRoute path={`/office/blocked-users`}>
                              <BlockedUsers windowSize={windowSize} />
                            </PrivateRoute>
                          }
                        {/* End of Admin Routes */}



                        <HeadedRoute path="/order/payment">
                          <OrderPayment windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/options">
                          <OrderOptions windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/documents">
                          <OrderDocuments windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/contact">
                          <OrderContact windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/type">
                          <OrderType windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/manualQuote/create">
                          <QuoteCreate windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/manualQuote/details">
                          <QuoteCreateDetails windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/manualQuote/service">
                          <QuoteService windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/quote/details">
                          <QuoteDetails windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/quote/options">
                          <QuoteOptions windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/quote/contact">
                          <QuoteContact windowSize={windowSize} />
                        </HeadedRoute>

                        <PrivateRoute path="/account/shipping">
                          <Shipping windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/invoices">
                          <AccountInvoices windowSize={windowSize} />
                        </PrivateRoute>

                        <HeadedRoute path="/account/quote/:quoteID">
                          <SingleQuote windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/account/order/:orderID">
                          <SingleOrder windowSize={windowSize} />
                        </HeadedRoute>

                        <PrivateRoute path="/account/orders">
                          <Orders windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/quotes">
                          <Quotes windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/profile">
                          <Profile windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/team-members">
                          <TeamMembers windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/settings">
                          <Setting windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/password">
                          <Password windowSize={windowSize} />
                        </PrivateRoute>

                        <HeadedRoute path="/make-payment/:item/:itemID">
                          <MakePayment windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/account/invoice/:itemID">
                          <SingleInvoice windowSize={windowSize} />
                        </HeadedRoute>

                        <HeadedRoute path="/join-team/:inviteID">
                          <JoinTeam windowSize={windowSize} />
                        </HeadedRoute>

                        {/* <HeadedRoute path="/guides">
                          <Guides windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/differences">
                          <DifferencesGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/count">
                          <CountGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/notarization">
                          <NotarizationGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/uscis">
                          <USCISGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/foreign">
                          <ForeignGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/apostille">
                          <ApostilleGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/process">
                          <ProcessGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/glossary">
                          <GlossaryGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/turnaround">
                          <TurnaroundGuide windowSize={windowSize} />
                        </HeadedRoute> */}

                        <HeadedRoute path="/terms">
                          <Terms windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/refunds">
                          <Refunds windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/privacy">
                          <Privacy windowSize={windowSize} />
                        </HeadedRoute>

                        {/* 
                            <HeadedRoute path="/jobs">
                          <Jobs windowSize={windowSize} />
                        </HeadedRoute>
                        */}

                        <HeadedRoute path="/about">
                          <About windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases/business-document-translation-services">
                          <Business windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/contact">
                          <Contact windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order-lookup">
                          <OrderLookup windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/verify">
                          <Verify windowSize={windowSize} />
                        </HeadedRoute>

                        {/* <HeadedRoute path="/sign-in">
                          <SignIn windowSize={windowSize} />
                        </HeadedRoute> */}

                        {/* <HeadedRoute path="/forgot">
                          <ForgotPassword windowSize={windowSize} />
                        </HeadedRoute> */}

                        {/* <HeadedRoute path="/create-account">
                          <CreateAccount windowSize={windowSize} />
                        </HeadedRoute> */}

                        <HeadedRoute path="/faq">
                          {/* <FAQ windowSize={windowSize} /> */}
                          <FAQs windowSize={windowSize}  />
                        </HeadedRoute>
                        <HeadedRoute path="/reviews">
                          <Reviews windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases/legal-document-translation">
                          <LegalDocumentTranslationPage windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases/medical-document-translation">
                          <MedicalDocumentTranslationPage windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases/certified-translation-of-birth-certificate">
                          <BirthCertificateDocumentTranslationPage windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases/immigration-document-translation-services">
                          <ImmigrationDocumentTranslationPage windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases/financial-document-translation">
                          <FinancialDocumentTranslationPage windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-cases">
                          <UseCasesPage windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/use-case">
                          <UseCasesRouter windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/languages">
                          <Languages windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/standard">
                          <Standard windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/interpretations">
                          <Interpretations windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/certified">
                          <Certified windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute exact path="/">
                          <Home windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute exact path="/partner">
                          <MarketingLandingPage windowSize={windowSize} />
                        </HeadedRoute>

                        {/* <HeadedRoute path="/language">
                          <LanguagesRouter windowSize={windowSize} />
                        </HeadedRoute> */}
                        {/* <HeadedRoute path="/documents">
                          <Documents windowSize={windowSize} />
                        </HeadedRoute> */}
                        {/* <HeadedRoute path="/document">
                          <DocumentsRouter windowSize={windowSize} />
                        </HeadedRoute> */}
                        <HeadedRoute path="*">
                          <NotFound loadingUser={loadingUser} loadingAuth={loadingAuth} loadingTeam={loadingTeam}/>
                        </HeadedRoute>
                      </Switch>
                    </Router>
                  </HelmetProvider>
                {/* </Elements> */}
              </div>
            </PayPalScriptProvider>
          </DataContext.Provider>
        </AuthContext.Provider>
      </NotificationContext.Provider>
    </TransitionContext.Provider>
    </>
  );
};

export default App;
